import { ArrowUpRight, Bookmark, Clock3, MapPin, MessageCircle, Sparkles } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import type { Athlete, Company } from '../../types/domain'
import { calculateMatch } from '../../lib/matching'
import { currency } from '../../lib/format'
import { useAppStore } from '../../store/AppStore'
import { MatchBadge, VerificationBadge } from '../ui/Badge'
import { Button } from '../ui/Button'

export function AthleteCard({ athlete, company, compact = false }: { athlete: Athlete; company?: Company; compact?: boolean }) {
  const { state, toggleFavorite, addToFolder, inviteAthleteToChat } = useAppStore()
  const navigate = useNavigate()
  const isCompany = state.currentUser?.role === 'company' && company
  const match = isCompany ? calculateMatch(athlete, company, state.matchingWeights) : null
  const favorite = state.favorites.includes(athlete.id)
  const connection = isCompany ? state.connections.find((item) => item.athleteId === athlete.id && item.companyId === company.id) : undefined
  const remaining = athlete.budgetNeeded - athlete.fundedAmount

  const save = () => {
    if (!favorite) addToFolder('Ma sélection', athlete.id)
    toggleFavorite(athlete.id)
  }

  return <article className={`athlete-card athlete-card--next ${compact ? 'athlete-card--compact' : ''}`}>
    <div className="athlete-card__media">
      <Link to={`/athletes/${athlete.slug}`} aria-label={`Voir le profil de ${athlete.firstName} ${athlete.lastName}`}><img src={athlete.cover} alt={`${athlete.firstName} ${athlete.lastName}, ${athlete.discipline}`} loading="lazy" /></Link>
      <div className="athlete-card__media-shade" />
      <div className="athlete-card__topline"><span className="live-pill"><i />Disponible</span>{athlete.premium && <span className="premium-ribbon"><Sparkles size={13} />Premium</span>}</div>
      <button className={`save-button ${favorite ? 'is-active' : ''}`} aria-label={favorite ? 'Retirer de la sélection' : 'Enregistrer le profil'} onClick={save}><Bookmark fill={favorite ? 'currentColor' : 'none'} /></button>
      <div className="athlete-card__identity"><img src={athlete.photo} alt="" /><div><small>{athlete.discipline} · {athlete.level}</small><h3><Link to={`/athletes/${athlete.slug}`}>{athlete.firstName} {athlete.lastName}</Link></h3></div></div>
    </div>
    <div className="athlete-card__body">
      <div className="athlete-card__signals">
        <span><MapPin size={15} />{athlete.city}</span>
        <span><Clock3 size={15} />Répond en {athlete.responseTimeHours < 24 ? `${athlete.responseTimeHours} h` : '1 jour'}</span>
        {match && <MatchBadge score={match.score} level={match.level} compact />}
      </div>
      <div className="athlete-card__request"><span>Recherche pour sa saison</span><strong>{currency(remaining)}</strong></div>
      <div className="athlete-card__offer"><span>Disponible pour</span><div className="tag-row">{athlete.contentTypes.slice(0, 3).map((item) => <span className="tag" key={item}>{item}</span>)}</div></div>
      <div className="athlete-card__verification"><VerificationBadge level={athlete.verified} compact /><span>{athlete.nextCompetition}</span></div>
      {connection && <span className="connection-status">{connection.status === 'conversation_started' ? 'Échange commencé' : connection.status === 'proposal_in_progress' ? 'Proposition en cours' : connection.status === 'partnership_created' ? 'Partenariat créé' : 'Intérêt envoyé'}</span>}
      <div className="athlete-card__actions">
        <Button variant="ghost" size="sm" onClick={save}>{favorite ? 'Enregistré' : 'Enregistrer'}</Button>
        <Button size="sm" onClick={() => navigate(`/athletes/${athlete.slug}`)}>Voir le profil <ArrowUpRight size={16} /></Button>
        {isCompany && <button className="icon-button" title="Inviter à échanger" onClick={() => { const id = inviteAthleteToChat(athlete.id); navigate(`/app/messages?conversation=${id}`) }}><MessageCircle /></button>}
      </div>
    </div>
  </article>
}
